import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ticketsApi, operatorsApi, categoriesApi, resolveAvatarUrl } from '../api'
import { useAuthStore, useStore } from '../store'
import Data from './Data'
import {
  Alert,
  Avatar,
  Badge,
  Beacon,
  Button,
  EmptyState,
  Input,
  Loader,
  Menu,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Toolbar,
} from '../ui'
import {
  HiArrowPath,
  HiArrowRight,
  HiArrowUturnLeft,
  HiChatBubbleLeftRight,
  HiCheck,
  HiCheckCircle,
  HiCircleStack,
  HiClipboardDocument,
  HiClock,
  HiEye,
  HiInbox,
  HiMagnifyingGlass,
  HiOutlineStar,
  HiQueueList,
  HiStar,
  HiUser,
} from 'react-icons/hi2'

// Bosqich (status) — DESIGN.md 4-bo'lim: neytral ton, rang faqat muhimlikda
const STATUS_LABEL = { new:'Yangi', accepted:'Qabul', in_progress:'Jarayonda', resolved:'Yechildi', closed:'Yopildi', waiting:'Kutmoqda' }
const PRI_TONE     = { critical:'critical', high:'high', medium:'medium', low:'low' }
const PRI_LABEL    = { critical:'Kritik', high:'Yuqori', medium:"O'rta", low:'Past' }

// Ro'yxatni jimgina yangilash oralig'i: WebSocket asosiy kanal bo'lgani uchun
// poling faqat zaxira — jadval qo'l ostida qimirlab turmasligi kerak
const POLL_MS = 30000

const TABS = [
  { id: 'in_progress', label: 'Jarayonda',           icon: HiArrowPath },
  { id: 'all',         label: 'Barchasi',            icon: HiQueueList },
  { id: 'unassigned',  label: 'Kutilayotgan',        icon: HiClock },
  { id: 'mine',        label: 'Mening',              icon: HiUser },
  { id: 'completed',   label: 'Bajarilgan',          icon: HiCheckCircle },
  { id: 'archive',     label: "Ma'lumotlar bazasi",  icon: HiCircleStack },
]

// Baho yulduzchalari — emoji o'rniga ikonka (DESIGN.md 8-bo'lim).
// Rang tashimaydi: to'ldirilgan yulduz --text-1, bo'shi --text-3.
function RatingStars({ score }) {
  const n = Math.max(0, Math.min(5, score || 0))
  return (
    <span className="ui-row ui-gap-4" role="img" aria-label={`Baho: ${n} / 5`}>
      {[1, 2, 3, 4, 5].map(i => (
        i <= n
          ? <HiStar key={i} size={12} aria-hidden="true" />
          : <HiOutlineStar key={i} size={12} aria-hidden="true" style={{ color: 'var(--ui-text-3)' }} />
      ))}
    </span>
  )
}

// O'qilmagan xabarlar hisoblagichi (yopilgan murojaatlarda ko'rsatilmaydi)
function UnreadBadge({ ticket }) {
  const n = ticket.unread_count || 0
  if (n <= 0 || ticket.status === 'resolved' || ticket.status === 'closed') return null
  return (
    <Badge tone="neutral" title="O'qilmagan xabarlar" className="ui-nums">
      {n > 99 ? '99+' : n}
    </Badge>
  )
}

// Kritik murojaatlar banneri — statik: miltillash yo'q (DESIGN.md 7-bo'lim),
// signal qizil chegara va qizil sarlavha orqali beriladi
function SosBanner({ tickets, onNavigate }) {
  const crits = tickets.filter(t => t.priority === 'critical' &&
    !['resolved', 'closed'].includes(t.status))
  if (!crits.length) return null
  return (
    <div style={{ marginBottom: 'var(--ui-sp-16)' }}>
      <Alert
        tone="danger"
        icon={Beacon}
        title={<span style={{ color: 'var(--ui-st-critical)' }}>KRITIK — {crits.length} ta shoshilinch murojaat!</span>}
      >
        <div className="ui-between ui-gap-12 ui-wrap">
          <span className="ui-mono ui-nums ui-truncate" style={{ fontSize: 'var(--ui-fs-xs)', color: 'var(--ui-text-2)' }}>
            {crits.map(t => `#${t.ticket_number}`).join(' · ')}
          </span>
          <Button variant="secondary" size="sm" icon={HiArrowRight} onClick={() => onNavigate('critical')}>
            Ko'rish
          </Button>
        </div>
      </Alert>
    </div>
  )
}

// ── Split-panel uchun ixcham ko'rinish ────────────────────
function EmbeddedTickets({ tickets, loading, total, activeTab, setActiveTab, search, setSearch, navigate, load }) {
  return (
    <div className="ui-col" style={{ height: '100%' }}>
      {/* Qidiruv + bo'limlar */}
      <div style={{
        flexShrink: 0,
        padding: 'var(--ui-sp-12)',
        borderBottom: '1px solid var(--ui-border)',
      }}>
        <div className="ui-row ui-gap-8">
          <div className="ui-grow">
            <Input
              size="sm"
              icon={HiMagnifyingGlass}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Qidirish..."
              aria-label="Murojaatlarni qidirish"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon={HiArrowPath}
            iconOnly
            aria-label="Yangilash"
            title="Yangilash"
            onClick={() => load()}
          />
        </div>
        <div style={{ marginTop: 'var(--ui-sp-8)' }}>
          <Tabs
            items={TABS.slice(0, 4)}
            value={activeTab}
            onChange={setActiveTab}
            variant="segmented"
          />
        </div>
      </div>

      {/* Kartochkalar ro'yxati */}
      <div className="ui-col ui-gap-8" style={{ flex: 1, overflowY: 'auto', padding: 'var(--ui-sp-12)' }}>
        {loading ? (
          // Ro'yxat qidirilmoqda/filtrlanmoqda — "searching" aynan shu kutish uchun
          <Loader state="searching" block />
        ) : tickets.length === 0 ? (
          <EmptyState icon={HiInbox} title="Murojaat topilmadi" />
        ) : tickets.map(t => (
          <div
            key={t.id}
            className="ui-card ui-col ui-gap-4"
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/ticket/${t.id}`)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/ticket/${t.id}`) }
            }}
            style={{
              padding: 'var(--ui-sp-12)',
              cursor: 'pointer',
              borderColor: t.priority === 'critical' ? 'var(--ui-st-critical)' : undefined,
            }}
          >
            <div className="ui-between ui-gap-8">
              <span className="ui-row ui-gap-4 ui-mono ui-nums" style={{ fontSize: 'var(--ui-fs-xs)', fontWeight: 600 }}>
                #{t.ticket_number}
                <UnreadBadge ticket={t} />
              </span>
              <span className="ui-row ui-gap-4">
                <Badge tone="neutral">{STATUS_LABEL[t.status] || t.status}</Badge>
                <Badge tone={PRI_TONE[t.priority] || 'neutral'}>{PRI_LABEL[t.priority] || '—'}</Badge>
              </span>
            </div>
            <div className="ui-truncate" style={{ fontSize: 'var(--ui-fs-sm)', fontWeight: 500 }}>
              {t.citizen?.full_name || 'Fuqaro'}
            </div>
            {t.operator_name && (
              <span className="ui-row ui-gap-4 ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>
                <HiUser size={12} aria-hidden="true" />
                {t.operator_name}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="ui-muted" style={{
        flexShrink: 0,
        padding: 'var(--ui-sp-8) var(--ui-sp-12)',
        borderTop: '1px solid var(--ui-border)',
        fontSize: 'var(--ui-fs-xs)',
      }}>
        Jami: <span className="ui-nums">{total}</span> ta murojaat
      </div>
    </div>
  )
}

export default function Tickets({ embedded }) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { triggerCriticalReload, addToast } = useStore()
  const isAdmin = user?.role === 'super_admin'
  const [tickets, setTickets]     = useState([])
  const [total, setTotal]         = useState(0)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)
  const [activeTab, setActiveTab] = useState('in_progress')
  const [search, setSearch]       = useState('')
  const [priority, setPriority]   = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [operatorId, setOperatorId] = useState('')
  const [page, setPage]           = useState(1)
  const [operators, setOperators] = useState([])
  const [categories, setCategories] = useState([])
  const [activeChat, setActiveChat] = useState(null)
  const PER_PAGE = embedded ? 50 : 20

  // ── Manzildagi filtrlar ────────────────────────────────────────
  // Bosh sahifadagi ko'rsatkich yoki turkum bosilganda shu sahifa FILTRLANGAN
  // holda ochilishi kerak. Ilgari manzildagi so'rov (`/tickets?priority=critical`)
  // o'qilmasdi — bosish sahifani ochardi-yu, ro'yxat o'zgarmasdi.
  //
  // Filtr kelganda bo'lim "Barchasi" ga o'tadi: standart bo'lim faqat
  // "Jarayonda" ni ko'rsatadi va kritiklarni ataylab chiqarib tashlaydi,
  // ya'ni so'ralgan murojaatlar ro'yxatga umuman tushmasdi.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const p = searchParams.get('priority') || ''
    const c = searchParams.get('category_id') || ''
    const o = searchParams.get('operator_id') || ''
    const unassigned = searchParams.get('unassigned') === 'true'
    if (!p && !c && !o && !unassigned) return
    setPriority(p)
    setCategoryId(c)
    setOperatorId(o)
    // "Tayinlanmagan" ro'yxati alohida bo'lim sifatida allaqachon bor —
    // yangi holat kiritilmaydi, shunchaki o'sha bo'lim ochiladi.
    setActiveTab(unassigned ? 'unassigned' : 'all')
    setPage(1)
    // Manzil tozalanadi: filtr endi sahifa holatida yashaydi. Aks holda
    // foydalanuvchi filtrni o'chirsa ham, ortga/yangilashda u qaytib kelardi.
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (isAdmin) operatorsApi.list().then(setOperators).catch(() => {})
    categoriesApi.list().then(setCategories).catch(() => {})
    if (!isAdmin) {
      ticketsApi.list({ mine: 'true', limit: 5 }).then(res => {
        const active = (res?.items || []).find(t =>
          t.status === 'accepted' || t.status === 'in_progress'
        )
        setActiveChat(active || null)
      }).catch(() => {})
    }
  }, [isAdmin])

  const load = useCallback(async (silent = false) => {
    if (!silent) { setLoading(true); setError(null) }
    try {
      const params = { skip: (page - 1) * PER_PAGE, limit: PER_PAGE, sort: 'activity' }
      if (search) params.search = search
      // Kritiklarni chiqarib tashlash faqat filtrsiz oddiy ro'yxatda ishlaydi:
      // qidiruvda yoki "Barchasi" bo'limida kritik murojaat ham topilishi shart
      if (priority) params.priority = priority
      else if (!search && activeTab !== 'all') params.exclude_priority = 'critical'
      if (activeTab === 'unassigned')  params.unassigned = 'true'
      else if (activeTab === 'mine')   params.mine = 'true'
      else if (activeTab === 'in_progress') params.status = 'in_progress'
      else if (activeTab === 'completed' || activeTab === 'resolved' || activeTab === 'closed') {
        params.exclude_status = 'new,accepted,in_progress,waiting'
      }
      // Barchasi tab so'ralganda hamma murojaatlar (shu jumladan bajarilganlar ham) ko'rinadi
      if (categoryId) params.category_id = categoryId
      if (operatorId) params.operator_id = operatorId

      if (activeTab === 'archive') {
        // Data komponenti o'zini o'zi yuklaydi — bu yerda ro'yxat bo'sh qoladi
        setTickets([])
        setTotal(0)
        setLoading(false)
        return
      }

      const res = await ticketsApi.list(params)
      setTickets(res?.items || [])
      setTotal(res?.total || 0)
    } catch (e) {
      if (!silent) setError(e.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [search, priority, categoryId, operatorId, activeTab, page, PER_PAGE])

  useEffect(() => { load() }, [load])

  // Ro'yxatni jimgina avtomatik yangilash (spinner ko'rsatmasdan).
  // Ochiq ⋯ menyu Menu komponentining ichki holati — qayta render uni yopmaydi.
  useEffect(() => {
    const id = setInterval(() => { load(true) }, POLL_MS)
    return () => clearInterval(id)
  }, [load])

  const handleAccept = async (ticketId) => {
    try {
      await ticketsApi.accept(ticketId)
      triggerCriticalReload()
      await load()
    } catch (err) {
      // alert() o'rniga global toast (DESIGN.md 10-bo'lim: kit alert() chaqirmaydi)
      addToast(err?.message || 'Qabul qilishda xato', 'critical')
    }
    navigate(`/ticket/${ticketId}`)
  }

  const handleCopyNumber = (num) => {
    const fail = () => addToast("Raqamdan nusxa olib bo'lmadi", 'critical')
    let task
    try { task = navigator.clipboard?.writeText?.(String(num)) } catch { task = null }
    if (!task) { fail(); return }
    task.then(() => addToast(`#${num} nusxalandi`, 'ticket')).catch(fail)
  }

  // ── Embedded (split-panel) ────────────────────────────────
  if (embedded) {
    return (
      <EmbeddedTickets
        tickets={tickets} loading={loading} total={total}
        activeTab={activeTab} setActiveTab={t => { setActiveTab(t); setPage(1) }}
        search={search} setSearch={v => { setSearch(v); setPage(1) }}
        navigate={navigate} load={load}
      />
    )
  }

  // ── To'liq rejim ──────────────────────────────────────────
  const tabItems = TABS.map(t => ({ ...t, count: t.id === activeTab ? total : undefined }))

  const columns = [
    {
      key: 'ticket_number',
      title: 'Raqam',
      width: '12%',
      render: t => (
        <span className="ui-row ui-gap-4">
          <button
            type="button"
            className="ui-link ui-mono ui-nums"
            style={{ background: 'transparent', border: 0, padding: 0, fontWeight: 600 }}
            onClick={() => navigate(`/ticket/${t.id}`)}
          >
            {t.ticket_number}
          </button>
          <UnreadBadge ticket={t} />
        </span>
      ),
    },
    {
      key: 'citizen',
      title: 'Fuqaro',
      width: '26%',
      render: t => (
        <div className="ui-col ui-gap-4" style={{ minWidth: 0, maxWidth: 280 }}>
          <span className="ui-truncate" style={{ fontSize: 'var(--ui-fs-sm)', fontWeight: 500 }}>
            {t.citizen?.full_name || 'Fuqaro'}
          </span>
          <span className="ui-mono ui-muted ui-truncate" style={{ fontSize: 'var(--ui-fs-xs)' }}>
            {t.citizen?.username ? `@${t.citizen.username}` : (t.citizen?.phone || '—')}
          </span>
          {t.last_message && (
            <span className="ui-muted ui-truncate" style={{ fontSize: 'var(--ui-fs-xs)' }}>
              {t.last_message_from === 'operator' && <HiArrowUturnLeft size={12} aria-hidden="true" style={{ display: 'inline', marginRight: 4 }} />}
              {t.last_message}
            </span>
          )}
          {(t.satisfaction_score > 0 || t.satisfaction_comment) && (
            <span className="ui-row ui-gap-8" style={{ minWidth: 0 }}>
              {t.satisfaction_score > 0 && <RatingStars score={t.satisfaction_score} />}
              {t.satisfaction_comment && (
                <span className="ui-row ui-gap-4 ui-muted ui-truncate" style={{ fontSize: 'var(--ui-fs-xs)' }}>
                  <HiChatBubbleLeftRight size={12} aria-hidden="true" />
                  <span className="ui-truncate">{t.satisfaction_comment}</span>
                </span>
              )}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      title: 'Kategoriya',
      width: '12%',
      render: t => (
        <span className="ui-truncate" style={{ fontSize: 'var(--ui-fs-xs)', color: 'var(--ui-text-2)' }}>
          {categories.find(c => String(c.id) === String(t.category_id))?.name_uz || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      width: '10%',
      render: t => <Badge tone="neutral">{STATUS_LABEL[t.status] || t.status}</Badge>,
    },
    {
      key: 'priority',
      title: 'Muhimlik',
      width: '10%',
      render: t => <Badge tone={PRI_TONE[t.priority] || 'neutral'}>{PRI_LABEL[t.priority] || '—'}</Badge>,
    },
    {
      key: 'operator',
      title: 'Operator',
      width: '14%',
      render: t => (t.operator_id && t.status !== 'new')
        ? (
          <span className="ui-row ui-gap-8" style={{ minWidth: 0 }}>
            <Avatar name={t.operator_name || 'Operator'} src={resolveAvatarUrl(t.operator_avatar)} size={24} />
            <span className="ui-truncate" style={{ fontSize: 'var(--ui-fs-xs)' }}>{t.operator_name || 'Operator'}</span>
          </span>
        )
        : <Badge tone="neutral" icon={HiClock}>Kutilayotgan</Badge>,
    },
    {
      key: 'actions',
      title: 'Amallar',
      align: 'right',
      render: t => {
        const needsAccept = (!t.operator_id || t.status === 'new') &&
          t.status !== 'resolved' && t.status !== 'closed'
        return (
          <div className="ui-row ui-gap-4" style={{ justifyContent: 'flex-end' }}>
            {needsAccept && (
              <Button variant="primary" size="sm" onClick={() => handleAccept(t.id)}>Qabul</Button>
            )}
            <Menu
              trigger="Amallar"
              align="end"
              items={[
                { label: 'Ochish', icon: HiEye, onClick: () => navigate(`/ticket/${t.id}`) },
                ...(needsAccept
                  ? [{ label: 'Qabul qilish', icon: HiCheck, onClick: () => handleAccept(t.id) }]
                  : []),
                { label: 'Raqamdan nusxa', icon: HiClipboardDocument, onClick: () => handleCopyNumber(t.ticket_number) },
              ]}
            />
          </div>
        )
      },
    },
  ]

  return (
    <div className="page-enter">
      <PageHeader
        title="Barcha murojaatlar"
        sub={<span className="ui-nums">Jami {total} ta murojaat</span>}
        actions={
          <Button
            variant="ghost"
            size="sm"
            icon={HiArrowPath}
            iconOnly
            aria-label="Yangilash"
            title="Yangilash"
            onClick={() => load()}
          />
        }
      />

      <Tabs items={tabItems} value={activeTab} onChange={t => { setActiveTab(t); setPage(1) }} />

      {activeTab === 'archive' ? (
        <div style={{ marginTop: 'var(--ui-sp-16)' }}>
          <Data hideHeader={true} />
        </div>
      ) : (
        <div style={{ marginTop: 'var(--ui-sp-16)' }}>
          {activeChat && !isAdmin && (
            <div style={{ marginBottom: 'var(--ui-sp-16)' }}>
              <Alert tone="info" icon={HiChatBubbleLeftRight} title={`Faol chat: #${activeChat.ticket_number}`}>
                <div className="ui-between ui-gap-12 ui-wrap">
                  <span className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>
                    Yangi chat boshlash uchun avval bu chatni tugatish kerak
                  </span>
                  <Button variant="primary" size="sm" icon={HiArrowRight}
                    onClick={() => navigate(`/ticket/${activeChat.id}`)}>
                    Chatga qaytish
                  </Button>
                </div>
              </Alert>
            </div>
          )}

          <SosBanner tickets={tickets} onNavigate={pri => { setPriority(pri); setPage(1) }} />

          <div style={{ marginBottom: 'var(--ui-sp-16)' }}>
            <Toolbar>
              <div className="ui-grow" style={{ minWidth: 200 }}>
                <Input
                  icon={HiMagnifyingGlass}
                  placeholder="Murojaat №, ism, username, matn..."
                  aria-label="Murojaatlarni qidirish"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1) }}
                />
              </div>
              <Select
                aria-label="Ustuvorlik bo'yicha filtr"
                style={{ width: 170 }}
                value={priority}
                onChange={e => { setPriority(e.target.value); setPage(1) }}
                options={[
                  { value: '', label: 'Barcha ustuvorlik' },
                  { value: 'critical', label: 'Kritik' },
                  { value: 'high', label: 'Yuqori' },
                  { value: 'medium', label: "O'rta" },
                  { value: 'low', label: 'Past' },
                ]}
              />
              <Select
                aria-label="Kategoriya bo'yicha filtr"
                style={{ width: 190 }}
                value={categoryId}
                onChange={e => { setCategoryId(e.target.value); setPage(1) }}
                options={[
                  { value: '', label: 'Barcha kategoriya' },
                  ...categories.map(c => ({ value: c.id, label: c.name_uz })),
                ]}
              />
              {isAdmin && (
                <Select
                  aria-label="Operator bo'yicha filtr"
                  style={{ width: 190 }}
                  value={operatorId}
                  onChange={e => { setOperatorId(e.target.value); setPage(1) }}
                  options={[
                    { value: '', label: 'Barcha operator' },
                    ...operators.map(op => ({ value: op.id, label: op.full_name })),
                  ]}
                />
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setSearch(''); setPriority(''); setCategoryId(''); setOperatorId(''); setActiveTab('all'); setPage(1) }}
              >
                Tozalash
              </Button>
              {/* Jadval yangilanayotganini bildiruvchi belgi: skeleton qatorlar
                  nimani kutayotganini aytmaydi, "searching" aytadi */}
              {loading && <Loader state="searching" size="sm" label={false} />}
            </Toolbar>
          </div>

          {error && (
            <div style={{ marginBottom: 'var(--ui-sp-16)' }}>
              <Alert tone="danger" title="Ma'lumotni yuklab bo'lmadi">
                <div className="ui-between ui-gap-12 ui-wrap">
                  <span style={{ fontSize: 'var(--ui-fs-xs)' }}>{error}</span>
                  <Button variant="secondary" size="sm" icon={HiArrowPath} onClick={() => load()}>
                    Qayta urinish
                  </Button>
                </div>
              </Alert>
            </div>
          )}

          <Table
            columns={columns}
            rows={tickets}
            loading={loading}
            loadingRows={6}
            empty={<EmptyState icon={HiInbox} title="Murojaat topilmadi" text="Filtrlarni o'zgartirib ko'ring." />}
          />

          <div style={{ marginTop: 'var(--ui-sp-12)' }}>
            <Pagination total={total} page={page} perPage={PER_PAGE} onChange={setPage} />
          </div>
        </div>
      )}
    </div>
  )
}
